import { useRecoilValue, useSetRecoilState } from 'recoil';
import {
  type BlockId,
  DEFAULT_MARGIN,
  studioBlockHeight,
  studioBlockCustomWidth,
  COLUMN_GAP,
  VIRTUALIZATION_SCROLL_BAR_PX,
} from 'venn-state';
import { Numbers } from 'venn-utils';
import { BlockContainer } from './BlockContainer';
import React, { forwardRef, useCallback, useMemo, useRef } from 'react';
import Measure, { type ContentRect } from 'react-measure';
import styled from 'styled-components';
import { BlockContext, BorderContainer, useAppPrintMode } from 'venn-components';

type StudioModuleBlockSwitchProps = {
  id: string;
  index: number;
  width: number;
};

export const StudioModuleBlockSwitch = (props: StudioModuleBlockSwitchProps) => {
  return (
    <StudioBlockContainer id={props.id}>
      <BlockContainer {...props} />
    </StudioBlockContainer>
  );
};

type ContainerProps = {
  id: BlockId;
  children: React.ReactNode;
};
const StudioBlockContainer = forwardRef<HTMLDivElement, ContainerProps>((props, ref) => {
  const { id, children } = props;

  const blockRef = useRef<HTMLDivElement>(null);
  const blockContextValue = useMemo(() => ({ blockRef, pageNumber: undefined }), [blockRef]);

  const setBlockHeight = useSetRecoilState(studioBlockHeight(id));
  const onResize = useCallback(
    (contentRect: ContentRect) =>
      setBlockHeight((currVal) => Math.ceil(Math.max(contentRect?.bounds?.height ?? 0, currVal))),
    [setBlockHeight],
  );
  const blockWidth = useRecoilValue(studioBlockCustomWidth(id));
  const { inPrintMode } = useAppPrintMode();

  return (
    <Measure bounds key={id} onResize={onResize} innerRef={ref}>
      {({ measureRef }) => (
        <div
          ref={measureRef}
          style={{
            alignSelf: 'start',
            width:
              blockWidth < 1
                ? `calc(${Numbers.safeFormatPercentage(blockWidth)} - ${(COLUMN_GAP - (inPrintMode ? 0 : VIRTUALIZATION_SCROLL_BAR_PX)) * blockWidth}px)`
                : '100%',
          }}
        >
          <BlockContext.Provider value={blockContextValue}>
            <BorderContainer id={id} ref={blockRef}>
              {children}
            </BorderContainer>
          </BlockContext.Provider>
          <BlockSpacer />
        </div>
      )}
    </Measure>
  );
});

StudioBlockContainer.displayName = 'StudioBlockContainer';

const BlockSpacer = styled.div`
  height: ${DEFAULT_MARGIN}px;
`;
